/**
 * SchoolInsightsPanel — school-level aggregate (Tier 2).
 *
 * Reads `/counsellors/school/insights` and renders a summary strip
 * with realism spread, top target countries, and headline stats
 * across every cohort every colleague owns. Rendered inside the
 * SchoolAccessPanel so heads of sixth see the year-group story
 * without switching cohorts.
 */

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, TrendingUp, Globe, Star, GraduationCap } from "lucide-react";

const REALISM_STYLE = {
  safe:    { chip: "bg-emerald-100 text-emerald-800", bar: "bg-emerald-500", label: "Safe" },
  match:   { chip: "bg-amber-100 text-amber-800",     bar: "bg-amber-500",   label: "Match" },
  reach:   { chip: "bg-rose-100 text-rose-800",       bar: "bg-rose-500",    label: "Reach" },
  unknown: { chip: "bg-stone-100 text-stone-500",     bar: "bg-stone-400",   label: "Unrated" },
};

function StatTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-md border border-stone-200 bg-white p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-underground-sc tracking-wider text-stone-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-stone-900">{value}</div>
    </div>
  );
}

function RealismSpread({ mix }) {
  const total = Object.values(mix || {}).reduce((s, n) => s + n, 0);
  if (!total) {
    return (
      <div className="text-[12px] text-stone-500" data-testid="school-realism-empty">
        Realism spread will appear once colleagues save picks to their students' shortlists.
      </div>
    );
  }
  const order = ["safe", "match", "reach", "unknown"];
  return (
    <div>
      <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 mb-1">
        Realism spread · {total} picks
      </div>
      <div className="flex h-3 rounded-full overflow-hidden border border-stone-200" data-testid="school-realism-bar">
        {order.map((k) => {
          const n = mix[k] || 0;
          if (!n) return null;
          const pct = Math.round((n / total) * 100);
          const s = REALISM_STYLE[k];
          return (
            <div
              key={k}
              className={`${s.bar}`}
              style={{ width: `${pct}%` }}
              title={`${s.label}: ${n} (${pct}%)`}
              data-testid={`school-realism-seg-${k}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {order.map((k) => {
          const n = mix[k] || 0;
          if (!n) return null;
          const pct = Math.round((n / total) * 100);
          const s = REALISM_STYLE[k];
          return (
            <span
              key={k}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono ${s.chip}`}
              data-testid={`school-realism-chip-${k}`}
            >
              {s.label} · {n} · {pct}%
            </span>
          );
        })}
      </div>
    </div>
  );
}

function TopCountries({ rows }) {
  if (!rows?.length) return null;
  const max = rows[0].n || 1;
  return (
    <div>
      <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 mb-2 flex items-center gap-1.5">
        <Globe className="w-3 h-3" /> Top target countries
      </div>
      <ul className="space-y-1" data-testid="school-top-countries">
        {rows.map((r) => (
          <li key={r.country} className="flex items-center gap-2 text-[12px]" data-testid={`school-country-${r.country}`}>
            <div className="w-32 truncate text-stone-700 font-medium">{r.country}</div>
            <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#4C1D95]" style={{ width: `${Math.round((r.n / max) * 100)}%` }} />
            </div>
            <div className="w-8 text-right font-mono text-stone-600">{r.n}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SchoolInsightsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/counsellors/school/insights")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, []);

  if (err) return null;
  if (!data) {
    return (
      <div className="mt-4 text-[11px] text-stone-400">Loading school insights…</div>
    );
  }

  const showEmpty = data.row_count === 0;

  return (
    <div className="mt-5 rounded-md border border-stone-200 bg-white p-4" data-testid="school-insights-panel">
      <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 mb-3">
        Year-group insights · every colleague's cohorts
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <StatTile
          icon={Users}
          label="Students"
          value={data.student_count || 0}
          testid="school-insight-students"
        />
        <StatTile
          icon={GraduationCap}
          label="Picks"
          value={data.row_count || 0}
          testid="school-insight-picks"
        />
        <StatTile
          icon={Star}
          label="Starred"
          value={data.starred_count || 0}
          testid="school-insight-starred"
        />
        <StatTile
          icon={TrendingUp}
          label="Avg fit"
          value={data.avg_match_score != null ? `${data.avg_match_score}%` : "—"}
          testid="school-insight-avg-match"
        />
      </div>
      {showEmpty ? (
        <div className="text-[12px] text-stone-500" data-testid="school-insights-empty">
          Insights will populate as colleagues generate shortlists for their students.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <RealismSpread mix={data.realism_mix} />
          <TopCountries rows={data.top_countries || []} />
        </div>
      )}
    </div>
  );
}
